import type { SelectedReason, TransferAssignment } from '../../../project_starburst';

export interface WorkItemRow {
  readonly case_id: string;
  readonly subtenant_id: string;
  readonly health_system_id: string;
  readonly createdat: string;
}

const NOT_FOUND = "Couldn't find this case's work item.";

const ASSIGNMENT_FIELDS = ['worker_id', 'user_id', 'username', 'first_name', 'last_name'] as const;
const blank = (v: unknown): boolean => typeof v !== 'string' || v.trim().length === 0;

export function validateTransferPayload(payload: {
  readonly recipient?: { readonly assignment?: Partial<TransferAssignment> };
  readonly reasons: readonly SelectedReason[];
  readonly note?: string;
  readonly caseContext?: string;
}): void {
  const assignment = payload.recipient?.assignment;
  if (!assignment || ASSIGNMENT_FIELDS.some((f) => blank(assignment[f]))) {
    throw new Error('The selected person is missing assignment details.');
  }
  if (payload.reasons.length < 1 || payload.reasons.length > 25) {
    throw new Error('Choose between 1 and 25 reasons.');
  }
  if (payload.reasons.some((r) => blank(r.key) || r.key.length > 64 || blank(r.label) || r.label.length > 256)) {
    throw new Error('A selected reason is not valid.');
  }
  if ((payload.note?.length ?? 0) > 1024) throw new Error('The note is too long (1024 characters max).');
  if ((payload.caseContext?.length ?? 0) > 2000) throw new Error('The case context is too long (2000 characters max).');
}

export function pickWorkItemRow(rows: unknown): WorkItemRow {
  if (!Array.isArray(rows) || rows.length !== 1) throw new Error(NOT_FOUND);
  const raw = rows[0];
  const d = raw != null && typeof raw === 'object' && (raw as { data?: unknown }).data != null
    ? ((raw as { data: Record<string, unknown> }).data)
    : (raw as Record<string, unknown>);
  const fields = ['case_id', 'subtenant_id', 'health_system_id', 'createdat'] as const;
  if (!d || fields.some((f) => d[f] == null || String(d[f]).length === 0)) throw new Error(NOT_FOUND);
  return {
    case_id: String(d.case_id),
    subtenant_id: String(d.subtenant_id),
    health_system_id: String(d.health_system_id),
    createdat: String(d.createdat)
  };
}

export function toCaseDetailsRow(
  workItem: WorkItemRow,
  recipient: TransferAssignment,
  payload: { readonly reasons: readonly SelectedReason[]; readonly note?: string; readonly caseContext?: string },
  processInstanceGuid: string,
  nowIso: string
) {
  return {
    owner_first_name: recipient.first_name,
    owner_last_name: recipient.last_name,
    assignee_first_name: recipient.first_name,
    assignee_last_name: recipient.last_name,
    process_instance_guid: processInstanceGuid,
    work_item_id: workItem.case_id,
    tenant_uid: workItem.subtenant_id,
    health_system_tenant_uid: workItem.health_system_id,
    work_item_created_timestamp: workItem.createdat,
    owner_username: recipient.username,
    owner_worker_id: recipient.worker_id,
    owner_user_id: recipient.user_id,
    owner_updated_timestamp: nowIso,
    assignee_username: recipient.username,
    assignee_updated_timestamp: nowIso,
    assignee_user_id: recipient.user_id,
    assignee_worker_id: recipient.worker_id,
    transfer_reasons: payload.reasons,
    ...(payload.note ? { note: payload.note } : {}),
    ...(payload.caseContext ? { case_context: payload.caseContext } : {})
  };
}
